/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
/* eslint-disable react/no-array-index-key */
import LoadingIcon from '@/components/LoadingIcon';
import useDict from '@/hooks/dict/useDict';
import { useLocales } from '@/Locales';
import { useFieldToLabel } from '@/pages/patient/hooks/useOcr';
import { useFieldStream, useOcrDetail, useSseSocket } from '@/pages/patient/hooks/useSseSocket';
import { httpReformatMarkdownParse } from '@/services/imageProcess';
import { copyText } from '@/utils';
import { CheckCircleFilled, FieldTimeOutlined, LoadingOutlined } from '@ant-design/icons';
import { Empty, message } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
// @ts-ignore
import MarkdownIt from 'markdown-it';
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import MdEditor, { Plugins } from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import { notCopy, whiteField } from '../ImageOcrParsing';
import { OcrContext } from '../PatientTabs/components/TabItem';
import styles from './index.less';

const tabItems = [
  { label: 'OCR识别结果', value: 'OCR' },
  { label: '智能整理结果', value: 'RESULT' },
];

const streamField = ['inspectionReport', 'geneticTesting', 'drugRecord']; // 不显示的字段

interface OcrParsingDetailProps {
  ocrConfig?: any;
  reportType?: string;
}

/**
 * @description: ocr解析详情
 * @author: KaifengLi
 * @version: v2.32.0
 * @Date: 2025-04-17 14:42:40
 */
const OcrParsingDetail = forwardRef((props: OcrParsingDetailProps, ref: any) => {
  const { ocrConfig, reportType } = props;

  const {
    setOcrConfig,
    filePaths,
    loading,
    setLoading,
    controllerRef,
    setControllerRef,
    ocrDetail,
    setOcrDetail,
  } = useContext(OcrContext);
  const { t } = useLocales();
  const dict = useDict();
  MdEditor.unuse(Plugins.ListOrdered);
  MdEditor.unuse(Plugins.ListUnordered);
  const mdParser = new MarkdownIt({});
  // 移除有序列表渲染规则
  mdParser.renderer.rules.ordered_list_open = (tokens: any) => {
    return '<p>';
  };
  mdParser.renderer.rules.ordered_list_close = (tokens: any) => {
    return '<p>';
  };
  mdParser.renderer.rules.unordered_list_open = (tokens: any) => {
    return '<p>';
  };
  mdParser.renderer.rules.unordered_list_close = (tokens: any) => {
    return '<p>';
  };
  mdParser.renderer.rules.list_item_open = (tokens: any, idx: number) => {
    // 这里可以根据需要自定义列表项的渲染，例如将其渲染为无序列表项
    return '<p>';
  };

  const [activeTab, setActiveTab] = useState(tabItems[0].value);
  const mdEditorRef = useRef<any>(null);

  const {
    setSseParams,
    reportText,
    setReportText,
    closeSSE,
    jsonLoading,
    setJsonLoading,
    textLoading,
    error,
    connectLoading,
  } = useSseSocket();
  const {
    fieldStream,
    canExtractFields,
    setCanExtractFields,
    fieldStreamLoading,
    setFieldStream,
    setFieldStreamLoading,
    setFieldStreamParams,
    fieldsToLabel,
    reformatGroup,
  } = useFieldStream((g) => {
    if (!g?.length) {
      setActiveTab('OCR');
    }
  });
  const { ocrRecogniText, setOcrDetailParams, setOcrRecogniText } = useOcrDetail();
  const fields = useFieldToLabel(ocrConfig?.data?.reportType);

  useEffect(() => {
    if (canExtractFields) {
      const timer = setTimeout(() => {
        console.log('没有需要合并的字段', fieldStream);
        const text = fieldStream?.map((item: any) => {
          return Object.values(item)
            ?.filter((el) => el) // 过滤掉空值
            ?.join('\n');
        });
        console.log('合并后的数据', text);
        httpReformatMarkdownParse({
          reportType: ocrConfig?.data?.reportType,
          markdowns: text,
        })
          .then((res: any) => {
            console.log('合并后的数据', res);
            setOcrDetail(res);
          })
          .catch((err: any) => {
            console.log(err);
          })
          .finally(() => {
            setCanExtractFields(false);
            setFieldStreamLoading(false);
          });
        clearTimeout(timer);
      }, 100);
    }
  }, [canExtractFields]);

  /**
   * 验证图片格式是否有效
   * @param filePath 文件路径或文件名
   * @returns 是否为有效图片格式
   */
  const isValidImageFormat = (filePath: string): boolean => {
    // 获取文件扩展名（转换为小写进行比较）
    const extension = filePath.split('.').pop()?.toLowerCase() || '';

    // 只允许这些图片格式
    const allowedFormats = ['jpeg', 'jpg', 'bmp', 'png'];

    return allowedFormats.includes(extension);
  };

  /**
   * 过滤图片路径，只保留有效格式
   * @param paths 图片路径数组
   * @returns 过滤后的有效图片路径数组
   */
  const filterValidImagePaths = (paths: string[]): string[] => {
    const validPaths = paths.filter(isValidImageFormat);

    // 如果过滤掉了一些路径，可以记录日志
    if (validPaths.length < paths.length) {
      console.warn(`过滤掉了 ${paths.length - validPaths.length} 个非法格式的图片文件`);
    }

    return validPaths;
  };

  useEffect(() => {
    setOcrDetailParams({
      imagePaths: filePaths ? filterValidImagePaths(filePaths?.split(',')) : [],
      reportType,
    });
    setFieldStream([]);
    setOcrDetail([]);
    setActiveTab('OCR');
  }, [filePaths]);

  useEffect(() => {
    if (ocrConfig?.data) {
      // console.log(streamField.includes(ocrConfig?.data?.reportType));
      // 直接流式输出
      if (streamField.includes(ocrConfig?.data?.reportType)) {
        setSseParams(ocrConfig?.data);
      } else {
        // 按title模块识别
        setFieldStreamParams({
          patientId: ocrConfig?.data?.patientId, // 患者编号
          reportType: ocrConfig?.data?.reportType, // 报告类型
          recordId: ocrConfig?.data?.recordId, // 报告编号
          recognitionTaskId: ocrConfig?.data?.recordId, // 识别任务id
          imagePaths: ocrConfig?.data?.filePaths, // 图片路径
        });
      }
      setActiveTab('RESULT');
      setOcrDetailParams({
        imagePaths: filterValidImagePaths(ocrConfig?.data?.filePaths),
        reportType,
      });
    } else {
      setSseParams(undefined);
      setLoading(false);
      setOcrDetailParams(undefined);
      closeSSE();
      setOcrRecogniText([]);
    }
  }, [ocrConfig]);

  useEffect(() => {
    // console.log({ textLoading, jsonLoading, fieldStreamLoading });
    setLoading(textLoading || jsonLoading || fieldStreamLoading);
  }, [textLoading, jsonLoading, fieldStreamLoading]);

  const handleCancel = () => {
    controllerRef?.abort();
    closeSSE();
    setOcrConfig({
      open: false,
    });
    setOcrDetail([]);
    setFieldStream([]);
    setActiveTab(tabItems[0].value);
    setJsonLoading(false);
    setSseParams(undefined);
    setOcrDetailParams(undefined);
    setReportText('');
    setControllerRef(null);
  };

  useImperativeHandle(ref, () => ({
    closeRequest: handleCancel,
  }));

  const handleCopy = (text: string) => {
    copyText(text);
    message.success(t('已复制'));
  };

  if (!ocrConfig.open && !ocrRecogniText?.length) return null;

  const ContainerText = (item: any, key: any) => {
    if (key === 'imagesType') {
      return dict?.imageType?.[item?.[key]];
    }

    if (key === 'type' && ocrConfig?.data?.reportType === 'inspectionReport') {
      return item?.[key] === '0' ? '肿瘤标志物' : '其他';
    }
    return item?.[key];
  };

  const Container = () => {
    if (ocrConfig?.data?.reportType === 'inspectionReport') {
      const inspectionFields = {
        doctor: '送检医生',
        receivedTime: '接收日期',
        reportTime: '报告日期',
        laboratoryTestItems: '',
      };

      return ocrDetail?.reformatResults?.map((item: any, index: number) => (
        <div key={`result_${index}`}>
          {ocrDetail?.reformatResults?.length > 1 && (
            <div className={styles.label} style={{ padding: '12px 0' }}>
              数据{index + 1}.
            </div>
          )}
          <div className={styles['image-ocr']}>
            {/* 渲染基本信息字段 */}
            {Object.keys(inspectionFields)?.map((key: any) => {
              if (key !== 'laboratoryTestItems' && item[key]) {
                return (
                  <div key={key} className={styles['parsing-container']}>
                    <div className={styles['parsing-title']}>
                      <span className={styles.label}>{fields?.[key]}</span>
                      {/* <a onClick={() => handleCopy(item[key])}>复制</a> */}
                    </div>
                    <div className={styles.container}>{ContainerText(item, key)}</div>
                  </div>
                );
              }
              return null;
            })}

            {/* 单独渲染检验项目，避免重复 */}
            {item?.laboratoryTestItems?.map((el: any, i: number) => (
              <div
                key={`laboratoryTestItems_${index}_${i}`}
                className={styles['parsing-container']}
              >
                <div className={styles['parsing-title']}>
                  <span className={styles.label}>
                    检验项目
                    {i + 1}
                  </span>
                </div>
                {Object.keys(fields).map(
                  (list) =>
                    el?.[list] && (
                      <div style={{ paddingLeft: 12 }} key={list}>
                        <div className={styles['parsing-title']}>
                          <span className={styles.label}>{fields?.[list]}</span>
                          <a onClick={() => handleCopy(el[list])}>复制</a>
                        </div>
                        <div className={styles.container}>{ContainerText(el, list)}</div>
                      </div>
                    ),
                )}
              </div>
            ))}
          </div>
        </div>
      ));
    }
    const filess =
      ocrConfig?.data?.reportType === 'geneticTesting' ||
      ocrConfig?.data?.reportType === 'drugRecord'
        ? fields
        : fieldsToLabel;

    return (
      ocrDetail &&
      ocrDetail?.reformatResults?.map((item: any, index: number) => (
        <div key={`${index}`}>
          {ocrDetail?.reformatResults?.length > 1 && (
            <div className={styles.label} style={{ padding: '12px 0' }}>
              数据{index + 1}.
            </div>
          )}
          <div className={styles['image-ocr']}>
            {Object.keys(filess)?.map(
              (key: any) =>
                !whiteField.includes(key) &&
                item[key] && (
                  <div key={key} className={styles['parsing-container']}>
                    <div className={styles['parsing-title']}>
                      <span className={styles.label}>{filess?.[key]}</span>
                      {!notCopy.includes(key) && item[key] && (
                        <a onClick={() => handleCopy(item[key])}>复制</a>
                      )}
                    </div>
                    <div className={styles.container}>{ContainerText(item, key)}</div>
                  </div>
                ),
            )}
          </div>
        </div>
      ))
    );
  };

  const renderContent = () => {
    if (activeTab === 'OCR') {
      return (
        <div className={styles.contentArea}>
          {/* OCR识别结果内容区，可替换为实际内容 */}
          {ocrRecogniText?.map((el, index) => (
            <MdEditor
              key={index}
              htmlClass={classNames('custom-html-style', styles['editor-html-style'])}
              markdownClass={classNames(styles['editor-md-style'])}
              value={el}
              readOnly
              view={{
                menu: false,
                md: false,
                html: true,
              }}
              renderHTML={(text) => mdParser.render(text)}
            />
          ))}

          {/* <div key={index} className={styles['ocr-text']}>
              {el}
            </div> */}
          {!ocrRecogniText.length && (
            <Empty style={{ paddingTop: 100 }} description={t('暂无OCR识别结果')} />
          )}
        </div>
      );
    }
    if (activeTab === 'RESULT') {
      return (
        <div className={styles.contentArea}>
          {textLoading || jsonLoading || fieldStreamLoading ? (
            <LoadingIcon
              description={connectLoading ? t('图片OCR识别中...') : t('字段结构化过程中...')}
            />
          ) : null}
          {/* 智能整理结果内容区，可替换为实际内容 */}
          {!!reportText && !ocrDetail?.reformatResults?.length && (
            <>
              {/* <ReactMarkdown className={styles['report-text']}>{reportText}</ReactMarkdown> */}
              <MdEditor
                value={reportText}
                readOnly
                htmlClass={classNames('custom-html-style', styles['editor-html-style'])}
                markdownClass={classNames(styles['editor-md-style'])}
                view={{
                  menu: false,
                  md: false,
                  html: true,
                }}
                renderHTML={(text) => mdParser.render(text)}
              />
            </>
          )}
          {Container()}
          {!ocrDetail?.reformatResults?.length &&
            fieldStream?.map((item: any, index: number) => (
              <MdEditor
                ref={mdEditorRef}
                key={index}
                htmlClass={classNames('custom-html-style', styles['editor-html-style'])}
                markdownClass={classNames(styles['editor-md-style'])}
                value={Object.keys(item)
                  ?.map((key: any) => `${item[key]}`)
                  .join('\n')}
                readOnly
                view={{
                  menu: false,
                  md: false,
                  html: true,
                }}
                renderHTML={(text) => mdParser.render(text)}
              />
            ))}
          {!(textLoading || jsonLoading || fieldStreamLoading) &&
            !fieldStream.length &&
            !ocrDetail?.reformatResults?.length && (
              <Empty style={{ paddingTop: 100 }} description={t('暂无识别结果')} />
            )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.ocrParsingDetail}>
      <div className={styles.tabsHeader}>
        {reportType !== 'otherInformation' ? (
          <>
            <div
              className={classNames(styles.tabItem, activeTab === 'OCR' && styles.active)}
              onClick={() => setActiveTab('OCR')}
            >
              OCR识别结果 {ocrRecogniText && <CheckCircleFilled style={{ color: '#0365bf' }} />}
            </div>
            {(ocrDetail?.reformatResults?.length > 0 ||
              textLoading ||
              jsonLoading ||
              fieldStreamLoading) && (
              <div
                className={classNames(styles.tabItem, activeTab === 'RESULT' && styles.active)}
                onClick={() => setActiveTab('RESULT')}
              >
                智能整理结果{' '}
                {(() => {
                  if (ocrDetail?.reformatResults?.length) {
                    return <CheckCircleFilled style={{ color: '#0365bf' }} />;
                  }
                  if (fieldStreamLoading) {
                    return <LoadingOutlined style={{ color: '#0365bf' }} />;
                  }
                  return <FieldTimeOutlined />;
                })()}
              </div>
            )}
          </>
        ) : (
          <div className={`${styles.tabItem} ${styles.active}`}>
            OCR识别结果 {ocrRecogniText && <CheckCircleFilled style={{ color: '#0365bf' }} />}
          </div>
        )}
      </div>
      <div className={styles.tabContent}>{renderContent()}</div>
    </div>
  );
});

export default OcrParsingDetail;
